import type { TranscriptEntry } from '@deskworks/shared-types';
import { t } from '@/lib/i18n';

/**
 * What an agent's step means for a person, in a sentence ("Looking at a Work Item", "Calling gitlab.sellrz.io"), instead
 * of the tool's name and its raw arguments (docs/features/inbox-activity-sessions.md#steps). `now`: as it happens;
 * otherwise as done. null: no better words than the technical ones.
 */
export function friendlyStep(e: TranscriptEntry, now: boolean): string | null {
  if (e.kind === 'command') return commandText(commandOf(e), now, e.data.description);
  if (e.kind !== 'tool_call' || !e.title) return null;
  const args = argsOf(e.data.arguments);
  // `mcp__<server>__<tool>` as some runtimes name it.
  const name = e.title.split('__').pop() ?? e.title;
  return deskworksTool(name, args, now) ?? runtimeTool(name, args, now) ?? connectionTool(name, e.data.server, now);
}

type Args = Record<string, unknown>;

/** Arguments as an object: JSON, or the Python-style dict some runtimes report (`{'tool': 'inbox'}`). */
export function argsOf(raw: unknown): Args {
  if (raw && typeof raw === 'object') return raw as Args;
  if (typeof raw !== 'string' || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Only the plain string values of the top level: enough to say what a call is about.
    const out: Args = {};
    for (const m of raw.matchAll(/['"](\w+)['"]\s*:\s*(?:'((?:[^'\\]|\\.)*)'|"((?:[^"\\]|\\.)*)")/g)) {
      if (!(m[1] in out)) out[m[1]] = m[2] ?? m[3];
    }
    return out;
  }
}

function text(args: Args, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = args[key];
    if (typeof value === 'string' && value.trim()) {
      const line = value.trim().split('\n')[0];
      return line.length > 70 ? `${line.slice(0, 70)}…` : line;
    }
  }
  return null;
}

/** A sentence with an optional "what it is about" after a colon. */
function about(sentence: string, detail: string | null) {
  return detail ? `${sentence}: ${detail}` : sentence;
}

function pick(now: boolean, running: string, done: string) {
  return now ? running : done;
}

// ---------------------------------------------------------------- Deskworks's own tools

function deskworksTool(name: string, args: Args, now: boolean): string | null {
  switch (name) {
    case 'call_account_tool':
      return accountCall(text(args, 'tool') ?? '', argsOf(args.arguments), now);
    case 'list_account_tools':
    case 'list_mcp_tools':
    case 'ToolSearch':
      return pick(now, t('Getting the right tools ready'), t('Got the right tools ready'));
    case 'get_account_overview':
      return pick(now, t('Looking over the whole account'), t('Looked over the whole account'));
    case 'get_workspace_overview':
      return pick(now, t('Looking over a workspace'), t('Looked over a workspace'));
    case 'get_desk':
    case 'read_desk_setup':
      return pick(now, t('Looking at how a desk is set up'), t('Looked at how a desk is set up'));
    case 'delegate_work':
    case 'create_work_item':
    case 'create_sub_item':
      return about(pick(now, t('Handing out work'), t('Handed out work')), text(args, 'title'));
    case 'ask_desk':
    case 'ask_team':
    case 'tell_agent':
      return about(pick(now, t('Asking a colleague'), t('Asked a colleague')), text(args, 'question', 'message'));
    case 'ask_question':
    case 'ask_person':
    case 'request_approval':
      return about(pick(now, t('Asking you'), t('Asked you')), text(args, 'question', 'title', 'message'));
    case 'remember':
    case 'write_memory':
    case 'settle_notes':
    case 'revoke_memory':
      return pick(now, t('Writing down what she learned'), t('Wrote down what she learned'));
    case 'recall':
    case 'read_journal':
      return about(pick(now, t('Remembering'), t('Remembered')), text(args, 'query'));
    case 'write_journal':
      return pick(now, t('Writing the journal'), t('Wrote the journal'));
    case 'get_draft':
    case 'add_change':
    case 'update_change':
    case 'remove_change':
    case 'set_draft_notes':
      return pick(now, t('Working on the draft'), t('Worked on the draft'));
    case 'report_goal_progress':
    case 'propose_goal_change':
      return pick(now, t('Checking the goals'), t('Checked the goals'));
    case 'set_mission':
    case 'list_missions':
      return pick(now, t('Keeping track of the task'), t('Kept track of the task'));
    case 'watch':
      return about(pick(now, t('Starting to watch'), t('Started to watch')), text(args, 'why'));
    case 'unwatch':
      return pick(now, t('Stopping to watch'), t('Stopped watching'));
    case 'report_activity':
    case 'update_plan':
    case 'save_progress':
      return pick(now, t('Noting the progress'), t('Noted the progress'));
    case 'send_message':
      return pick(now, t('Writing a message'), t('Wrote a message'));
    case 'list_tables':
    case 'query':
      return pick(now, t('Looking into the data'), t('Looked into the data'));
    case 'get_view':
    case 'preview_view':
    case 'save_view':
      return pick(now, t('Working on a view'), t('Worked on a view'));
    case 'load_skill':
    case 'read_skill_file':
    case 'get_skill':
      return about(pick(now, t('Reading a skill'), t('Read a skill')), text(args, 'name'));
    case 'list_resources':
    case 'read_resource':
      return pick(now, t('Reading the documents'), t('Read the documents'));
    case 'read_playbook':
      return pick(now, t('Reading the playbook'), t('Read the playbook'));
    case 'save_playbook':
    case 'save_desk_skill':
    case 'update_desk_agent':
    case 'create_desk_agent':
    case 'update_desk_settings':
      return pick(now, t('Improving the desk'), t('Improved the desk'));
    case 'clone_repository':
      return pick(now, t('Fetching the code'), t('Fetched the code'));
    case 'merge_branch':
      return pick(now, t('Merging the changes'), t('Merged the changes'));
    case 'complete_work_item':
    case 'finish':
    case 'check_done':
      return pick(now, t('Wrapping up'), t('Wrapped up'));
    case 'decide':
      return pick(now, t('Deciding an approval'), t('Decided an approval'));
    case 'call_connection':
      return about(pick(now, t('Calling a connected system'), t('Called a connected system')), text(args, 'path'));
    default:
      return null;
  }
}

/** Areas of the account's API (`<area>_<action>`), as what a call reads or changes. */
const AREAS: [prefix: string, thing: () => string][] = [
  ['accounts_inbox', () => t('the inbox')],
  ['accounts_approvals', () => t('the approvals')],
  ['accounts_activity', () => t('what happened')],
  ['accounts', () => t('the account')],
  ['work_items', () => t('Work Items')],
  ['desk_files', () => t('the desk’s files')],
  ['desk_store', () => t('the Desk Store')],
  ['desks', () => t('a desk')],
  ['conversations', () => t('a chat')],
  ['connections', () => t('a connection')],
  ['agents', () => t('an agent')],
  ['skills', () => t('a skill')],
  ['views', () => t('a view')],
  ['workspace_db', () => t('the data')],
  ['resources', () => t('the documents')],
  ['actions', () => t('the approvals')],
  ['setup', () => t('the setup')],
  ['run_secrets', () => t('a secret')],
  ['runtime', () => t('a runtime')],
  ['reports', () => t('the reports')],
  ['sessions', () => t('an agent’s session')],
  ['assets', () => t('a file')],
  ['usage', () => t('the usage')],
];

/** One call of the account's API by VICKI (`call_account_tool`), from the route's name. */
function accountCall(tool: string, args: Args, now: boolean): string {
  const area = AREAS.find(([prefix]) => tool === prefix || tool.startsWith(`${prefix}_`));
  const thing = area ? area[1]() : t('the account');
  const action = area ? tool.slice(area[0].length + 1) : tool;
  const detail = text(args, 'title', 'name', 'query', 'message');
  if (/^(remove|delete)/.test(action)) return about(pick(now, t('Removing {thing}', { thing }), t('Removed {thing}', { thing })), detail);
  if (/^(create|add|clone|install|upload|start)/.test(action)) return about(pick(now, t('Creating {thing}', { thing }), t('Created {thing}', { thing })), detail);
  if (/message/.test(action)) return about(pick(now, t('Writing a message'), t('Wrote a message')), detail);
  if (/^(approve|reject|decide)/.test(action)) return pick(now, t('Deciding an approval'), t('Decided an approval'));
  if (/^(retry|resume|trigger|ready|continue)/.test(action)) return pick(now, t('Getting {thing} going again', { thing }), t('Got {thing} going again', { thing }));
  if (/^(update|put|set|save|move|pause|rename)/.test(action)) return about(pick(now, t('Changing {thing}', { thing }), t('Changed {thing}', { thing })), detail);
  return about(pick(now, t('Looking at {thing}', { thing }), t('Looked at {thing}', { thing })), detail);
}

// ---------------------------------------------------------------- the runtime's own tools

function fileName(args: Args) {
  const path = text(args, 'file_path', 'path', 'notebook_path');
  return path ? path.split('/').pop() || path : null;
}

function runtimeTool(name: string, args: Args, now: boolean): string | null {
  switch (name) {
    case 'Read':
    case 'read_file':
    case 'list_files':
      return about(pick(now, t('Reading'), t('Read')), fileName(args));
    case 'Write':
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit':
    case 'write_file':
    case 'edit_file':
      return about(pick(now, t('Writing'), t('Wrote')), fileName(args));
    case 'Grep':
    case 'Glob':
      return about(pick(now, t('Searching the files'), t('Searched the files')), text(args, 'pattern'));
    case 'WebSearch':
      return about(pick(now, t('Searching the web'), t('Searched the web')), text(args, 'query'));
    case 'WebFetch':
      return about(pick(now, t('Opening a web page'), t('Opened a web page')), host(text(args, 'url')));
    case 'TodoWrite':
      return pick(now, t('Planning the next steps'), t('Planned the next steps'));
    case 'Task':
    case 'Agent':
      return about(pick(now, t('Getting help with a part of it'), t('Got help with a part of it')), text(args, 'description'));
    default:
      return null;
  }
}

/** A tool of a connected system (GitLab, Google Analytics …): its words, without underscores. */
function connectionTool(name: string, server: unknown, now: boolean): string {
  const words = name.replace(/[_-]+/g, ' ').trim();
  const system = typeof server === 'string' && server && server !== 'deskworks' && server !== 'None' ? server : null;
  if (system) return t('In {system}: {what}', { system, what: words });
  return pick(now, t('Using: {what}', { what: words }), t('Used: {what}', { what: words }));
}

// ---------------------------------------------------------------- commands

function commandOf(e: TranscriptEntry): string {
  return (typeof e.data.command === 'string' && e.data.command) || e.title || '';
}

function host(url: string | null) {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * A shell command in words, by what it does (the runtime's own description first). The command itself stays one click
 * away in the steps list.
 */
export function commandText(command: string, now: boolean, description?: unknown): string {
  if (typeof description === 'string' && description && description !== 'None' && description !== 'No-op') return description;
  const cmd = command.replace(/^cd\s+\S+\s*(&&|;)\s*/, '').trim();
  const first = cmd.split(/\s+/)[0]?.split('/').pop() ?? '';
  const url = cmd.match(/https?:\/\/[^\s'"]+/)?.[0] ?? null;
  if (first === 'curl' || first === 'wget' || first === 'http') {
    return about(pick(now, t('Calling a web address'), t('Called a web address')), host(url));
  }
  if (first === 'git') return pick(now, t('Working with the code history'), t('Worked with the code history'));
  if (first === 'kubectl' || first === 'helm') return pick(now, t('Looking at the cluster'), t('Looked at the cluster'));
  if (['jq', 'python', 'python3', 'node', 'awk', 'sed'].includes(first)) return pick(now, t('Evaluating data'), t('Evaluated data'));
  if (['ls', 'cat', 'head', 'tail', 'grep', 'rg', 'find', 'wc', 'tree'].includes(first)) return pick(now, t('Looking through files'), t('Looked through files'));
  if (['npm', 'pnpm', 'yarn', 'npx', 'pytest', 'make', 'tsc', 'go', 'cargo'].includes(first)) return pick(now, t('Building and testing'), t('Built and tested'));
  if (first === 'echo' || first === 'true' || first === 'sleep') return pick(now, t('Getting ready'), t('Got ready'));
  return pick(now, t('Running a command'), t('Ran a command'));
}
